import React, { useState, useEffect } from "react";
import { FormLayout } from "@components/Forms/FormLayout/FormLayout";
import { FormMessages } from "@components/Forms/FormMessages/FormMessages";
import { UserInfoFields } from "@components/Forms/UserInfoFields/UserInfoFields";
import { SocialAuth } from "@components/SocialAuth/SocialAuth";
import { useFormSubmit } from "@hooks/useFormSubmit";
import { StarRating } from "@components/StarRating/StarRating";
import { FileUpload } from "@components/FileUpload/FileUpload";
import TextField from "@components/TextField/TextField";
import SubmitButton from "@components/Buttons/SubmitButton/SubmitButton";
import userAuthService from "@api/userAuthService";
import feedbackService from "@api/feedbackService";
import { FormPersistence } from "@utils/FormPersistenceUtil";
// import debugService from "@api/debugService";
import styles from "./ReviewForm.module.css";

export interface ReviewFormData {
  rating: number;
  advantages: string;
  disadvantages: string;
  reviewText: string;
  recommend: string;
  name: string;
  email: string;
}

const initialFormData: ReviewFormData = {
  rating: 0,
  advantages: "",
  disadvantages: "",
  reviewText: "",
  recommend: "yes",
  name: "",
  email: "",
};

interface ReviewFormProps {
  productId?: string;
  productName?: string;
  onModalClose?: () => void;
}

const ReviewForm: React.FC<ReviewFormProps> = ({
  productId,
  productName = "",
  onModalClose,
}) => {
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [videoFiles, setVideoFiles] = useState<File[]>([]);
  const [photoUploadError, setPhotoUploadError] = useState<string>("");
  const [videoUploadError, setVideoUploadError] = useState<string>("");
  const [socialAuthError, setSocialAuthError] = useState<string>("");
  const [isFormVisible, setIsFormVisible] = useState<boolean>(true);
  const [isSuccessMessageVisible, setIsSuccessMessageVisible] =
    useState<boolean>(false);

  // Загружаем сохраненные данные из localStorage
  const loadSavedFormData = (): ReviewFormData => {
    const savedData = FormPersistence.getReviewForm(productId);
    if (savedData) {
      return {
        ...initialFormData,
        ...savedData,
      };
    }

    // Если нет сохраненных данных, попробуем взять из данных пользователя
    const userData = userAuthService.getUserData();
    if (userData) {
      return {
        ...initialFormData,
        name: userData.name || "",
        email: userData.email || "",
      };
    }

    return initialFormData;
  };

  const {
    formData,
    setFormData,
    handleInputChange,
    isSubmitting,
    submitError,
    submitSuccess,
    handleSubmit: submitForm,
    isAttemptedSubmit,
    isFormValid,
  } = useFormSubmit<ReviewFormData>({
    initialData: loadSavedFormData(),
    validateForm: (data) => {
      return (
        data.rating > 0 &&
        data.reviewText.trim() !== "" &&
        data.name.trim() !== "" &&
        data.email.trim() !== ""
      );
    },
    onSubmit: async (data) => {
      try {
        // Загрузить фото, если есть
        let photoUrls: string[] = [];
        if (photoFiles.length) {
          try {
            photoUrls = await feedbackService.uploadFiles(photoFiles);
          } catch (error) {
            console.log("Error uploading photo files: ", error);
            // debugService.error("Error uploading photo files", { error });
            return {
              success: false,
              message: "Произошла ошибка при загрузке фотографий",
            };
          }
        }

        // Загрузить видео, если есть
        let videoUrls: string[] = [];
        if (videoFiles.length) {
          try {
            videoUrls = await feedbackService.uploadFiles(videoFiles);
          } catch (error) {
            console.log("Error uploading video files: ", error);
            // debugService.error("Error uploading video files", { error });
            return {
              success: false,
              message: "Произошла ошибка при загрузке видео",
            };
          }
        }

        // Отправить отзыв
        await feedbackService.submitReview({
          name: data.name,
          email: data.email,
          product_id: productId,
          product_name: productName,
          text: data.reviewText,
          rating: data.rating,
          photos: photoUrls,
          videos: videoUrls,
          pros: data.advantages,
          cons: data.disadvantages,
          optional_fields: {
            recommend: data.recommend === "yes",
          },
        });

        // Очищаем сохраненные данные после успешной отправки
        FormPersistence.clearReviewForm();

        return {
          success: true,
          message: "Спасибо! Ваш отзыв успешно отправлен",
        };
      } catch (error) {
        console.log("Error in review submission: ", error);
        // debugService.error("Error in review submission", { error });
        return {
          success: false,
          message: "Произошла ошибка при отправке отзыва",
        };
      }
    },
    successMessage: "Спасибо! Ваш отзыв успешно отправлен",
    errorMessage: "Произошла ошибка при отправке отзыва",
  });

  // Обертка для handleSubmit, чтобы очистить localStorage после успешной отправки
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    submitForm(e);
  };

  // Сохраняем данные формы при каждом изменении
  useEffect(() => {
    // Не сохраняем пустую форму или если уже отправлено успешно
    if (
      !submitSuccess &&
      (formData.reviewText ||
        formData.advantages ||
        formData.disadvantages ||
        formData.rating > 0)
    ) {
      FormPersistence.saveReviewForm(formData, productId);
    }
  }, [formData, submitSuccess, productId]);

  // Закрыть форму сразу после успешной отправки и показать сообщение об успехе
  useEffect(() => {
    if (submitSuccess) {
      // Сразу закрыть форму и показать сообщение
      setIsFormVisible(false);
      setIsSuccessMessageVisible(true);

      // Закрыть сообщение об успехе через 3 секунды
      const timer = setTimeout(() => {
        setIsSuccessMessageVisible(false);
        // Close the parent modal
        if (onModalClose) {
          onModalClose();
        }
      }, 3000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [submitSuccess, onModalClose]);

  const handlePhotoFileError = (error: string) => {
    setPhotoUploadError(error);
    setTimeout(() => setPhotoUploadError(""), 5000);
  };

  const handleVideoFileError = (error: string) => {
    setVideoUploadError(error);
    setTimeout(() => setVideoUploadError(""), 5000);
  };

  // Обработчик успешной авторизации через соцсеть
  const handleSocialAuth = (userData: any) => {
    console.log("Received user data from social auth:", userData);

    // Обновляем данные формы
    setFormData((prev) => ({
      ...prev,
      name: userData.name || prev.name,
      email: userData.email || prev.email,
    }));
  };

  // Обработчик ошибки авторизации через соцсеть
  const handleSocialAuthError = (error: string) => {
    console.error("Social auth error:", error);
    setSocialAuthError(error);
    setTimeout(() => setSocialAuthError(""), 5000);
  };

  return (
    <>
      {isFormVisible ? (
        <FormLayout title="Отзыв о товаре" onSubmit={handleSubmit}>
          <FormMessages success={submitSuccess} error={submitError} />
          <div className={styles.ratingContainer}>
            <label className={styles.ratingLabel}>Ваша оценка*</label>
            <StarRating
              rating={formData.rating}
              interactive={true}
              onChange={(value: number) =>
                setFormData((prev) => ({ ...prev, rating: value }))
              }
            />
            {isAttemptedSubmit && formData.rating === 0 && (
              <p className={styles.errorMessage}>Необходимо поставить оценку</p>
            )}
          </div>

          <TextField
            title="Достоинства"
            placeholder="Опишите достоинства товара"
            name="advantages"
            value={formData.advantages}
            onChange={handleInputChange}
            multiline
            rows={3}
          />

          <TextField
            title="Недостатки"
            placeholder="Опишите недостатки товара"
            name="disadvantages"
            value={formData.disadvantages}
            onChange={handleInputChange}
            multiline
            rows={3}
          />

          <TextField
            title="Текст отзыва"
            placeholder="Напишите Ваш отзыв о товаре"
            name="reviewText"
            value={formData.reviewText}
            onChange={handleInputChange}
            required
            multiline
            rows={4}
            error={
              isAttemptedSubmit && formData.reviewText.trim() === ""
                ? "Необходимо заполнить текст отзыва"
                : ""
            }
          />

          <div className={styles.mediaSection}>
            <h3 className={styles.sectionTitle}>Фотографии</h3>
            <div className={styles.uploadSection}>
              <FormMessages error={photoUploadError} />
              <FileUpload
                files={photoFiles}
                onFilesChange={setPhotoFiles}
                type="image"
                onError={handlePhotoFileError}
              />
            </div>
          </div>

          <div className={styles.mediaSection}>
            <h3 className={styles.sectionTitle}>Видео</h3>
            <div className={styles.uploadSection}>
              <FormMessages error={videoUploadError} />
              <FileUpload
                files={videoFiles}
                onFilesChange={setVideoFiles}
                type="video"
                onError={handleVideoFileError}
              />
            </div>
          </div>

          <div className={styles.recommendSection}>
            <label>Рекомендуете товар?</label>
            <div className={styles.radioGroup}>
              <label>
                <input
                  type="radio"
                  name="recommend"
                  value="yes"
                  checked={formData.recommend === "yes"}
                  onChange={handleInputChange}
                />
                Да
              </label>
              <label>
                <input
                  type="radio"
                  name="recommend"
                  value="no"
                  checked={formData.recommend === "no"}
                  onChange={handleInputChange}
                />
                Нет
              </label>
            </div>
          </div>

          <div className={styles.userInfoSection}>
            <UserInfoFields
              name={formData.name}
              email={formData.email}
              onNameChange={(value) =>
                setFormData((prev) => ({ ...prev, name: value }))
              }
              onEmailChange={(value) =>
                setFormData((prev) => ({ ...prev, email: value }))
              }
              isAttemptedSubmit={isAttemptedSubmit}
            />
          </div>

          <div className={styles.socialAuthSection}>
            {socialAuthError && <FormMessages error={socialAuthError} />}
            <SocialAuth
              onAuth={handleSocialAuth}
              onError={handleSocialAuthError}
            />
          </div>
          <div className={styles.fixedSubmitSection}>
            <SubmitButton
              text="Отправить отзыв"
              loading={isSubmitting}
              disabled={isSubmitting || !isFormValid()}
            />
            <FormMessages error={submitError} success={submitSuccess} />
          </div>
        </FormLayout>
      ) : isSuccessMessageVisible ? (
        <div className={styles.successContainer}>
          <h2 className={styles.successTitle}>Спасибо!</h2>
          <p className={styles.successMessage}>
            Ваш отзыв успешно отправлен и будет опубликован после проверки.
          </p>
        </div>
      ) : null}
    </>
  );
};

export default ReviewForm;
